import { useMemo, useState } from 'react'
import { BaseTable } from 'Features/Table/BaseTable.view'
import { ExtendedGridColDef } from 'Features/Table/BulkUpdateModal'
import { GridRenderCellParams } from '@mui/x-data-grid-premium'
import {
  SheduleTableProps,
  WorkstationDownTimeRowType,
} from './Scheduler.types'
import { Box, Tooltip } from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import ChatBubbleIcon from '@mui/icons-material/ChatBubble'
import {
  DownTimeMiniTimeline,
  DownTimeWindowsDialog,
} from './DownTimeWindowsEditor'
import { TOKENS } from './SchedulerDesign'
import {
  commentsFor,
  downtimeAnchor,
  useCommentIndex,
  useCurrentUserEmail,
} from './ScheduleComments.anchor'
import { COMMENT_ACCENT } from './ScheduleCommentPopover.view'

const RESOURCE_TYPE_OPTIONS = ['kitchen', 'line', 'cip', 'changeover_asset']

const dataConfig = {
  required: ['resource', 'downTime'],
  optional: ['resourceType', 'site_location', 'resourceGroupCode'],
}

type SchedulerWorkstationDownTimeTableProps =
  SheduleTableProps<WorkstationDownTimeRowType> & {
    onAddRow?: () => void
    /** Identify the schedule version so downtime rows can carry comments.
     *  All three are needed together; without them the comments section and
     *  the cell indicator are simply absent. */
    schedulerId?: string
    iteration?: string
    selectedWeek?: number
  }

export const SchedulerWorkstationDownTimeTable = ({
  data,
  isLoading,
  updateTableData,
  uploadScheduleData,
  isUploading,
  isSaving,
  updateRowsStatus,
  predicate,
  deleteTableData,
  isDeleting,
  onAddRow,
  schedulerId,
  iteration,
  selectedWeek,
}: SchedulerWorkstationDownTimeTableProps) => {
  // Handle data updates for BaseTable
  const handleDataUpdate = (
    changedRows: Record<number, WorkstationDownTimeRowType>
  ) => {
    updateTableData(changedRows)
  }

  // The downTime field is edited via a dedicated dialog (drag-to-create
  // timeline + numeric fields) rather than typing "(96,108)" into a text
  // cell — see DownTimeWindowsEditor.
  const [editingRow, setEditingRow] =
    useState<WorkstationDownTimeRowType | null>(null)

  // Comments on downtime are per resource, per week — one query for the whole
  // grid, read per row out of the index.
  const commentsEnabled = Boolean(schedulerId && iteration)
  const { byAnchor: commentIndex } = useCommentIndex(
    schedulerId,
    iteration,
    selectedWeek ?? 1
  )
  const currentUserEmail = useCurrentUserEmail()
  const editingComments = editingRow
    ? commentsFor(
        commentIndex,
        downtimeAnchor(editingRow.resource, editingRow.resourceType),
        selectedWeek ?? 1
      )
    : []

  const columns: ExtendedGridColDef[] = useMemo(
    () => [
      {
        headerName: 'Resource code',
        field: 'resource',
        flex: 1,
        type: 'string',
        editable: true,
      },
      {
        // One value for every week (edits are copied to every week's row).
        // Blank is stored as blank and shown as the resource's own code: each
        // resource is its own group until one is typed.
        headerName: 'Resource group code',
        field: 'resourceGroupCode',
        type: 'string',
        flex: 1,
        editable: true,
        description:
          "The group this resource belongs to. Defaults to the resource code. A packaging route's line columns name a line group, and the scheduler packs on whichever line in the group can start earliest. The same group applies in every week: changing it here changes it for all weeks.",
        valueGetter: (
          value: string | null | undefined,
          row: WorkstationDownTimeRowType
        ) =>
          value != null && String(value).trim() !== ''
            ? value
            : row?.resource ?? '',
      },
      {
        headerName: 'Resource Type',
        field: 'resourceType',
        type: 'singleSelect',
        valueOptions: RESOURCE_TYPE_OPTIONS,
        flex: 1,
        editable: true,
        renderCell: (params: any) => {
          if (params.value === 'changeover_asset') {
            return (
              <Tooltip title="A dedicated cleaning crew that can perform CIP/changeover during unit downtime">
                <span>{params.value}</span>
              </Tooltip>
            )
          }
          return params.value
        },
      },
      {
        headerName: 'Down Time',
        field: 'downTime',
        type: 'string',
        flex: 1,
        // Not inline-editable — opens DownTimeWindowsDialog instead.
        renderCell: (params: GridRenderCellParams) => {
          const row = params.row as WorkstationDownTimeRowType
          const rowComments = commentsFor(
            commentIndex,
            downtimeAnchor(row.resource, row.resourceType),
            selectedWeek ?? 1
          )
          return (
            <Box
              onClick={() => setEditingRow(row)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                width: '100%',
                height: '100%',
                cursor: 'pointer',
                '&:hover .downtime-edit-icon': { opacity: 1 },
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <DownTimeMiniTimeline value={params.value as string} />
              </Box>
              {rowComments.length > 0 && (
                <Tooltip
                  title={
                    rowComments.length === 1
                      ? '1 comment on this downtime'
                      : `${rowComments.length} comments on this downtime`
                  }
                >
                  <ChatBubbleIcon
                    sx={{
                      fontSize: 12,
                      flexShrink: 0,
                      color: rowComments.every((c) => c.resolved)
                        ? TOKENS.ink300
                        : COMMENT_ACCENT,
                    }}
                  />
                </Tooltip>
              )}
              <EditIcon
                className="downtime-edit-icon"
                sx={{
                  fontSize: 14,
                  color: TOKENS.ink400,
                  opacity: 0,
                  flexShrink: 0,
                }}
              />
            </Box>
          )
        },
      },
      {
        headerName: 'Site Location',
        field: 'site_location',
        flex: 1,
        type: 'string',
        editable: true,
      },
    ],

    // commentIndex/selectedWeek are read by the Down Time cell renderer, so
    // the columns have to be rebuilt when a comment lands or the week changes.
    [data, commentIndex, selectedWeek]
  )

  return (
    <Box>
      <BaseTable
        data={data ?? []}
        columns={columns}
        isLoading={isLoading}
        updateTableData={handleDataUpdate}
        uploadData={uploadScheduleData}
        isUploading={isUploading}
        isSaving={isSaving}
        checkBoxSelection={true}
        showTableToolBar={true}
        downloadFileName="SchedulerWorkstationDownTime"
        onDeleteRows={deleteTableData}
        isDeleting={isDeleting}
        multiStageUpload={{
          uploadFile: uploadScheduleData,
          isUploading: isUploading,
          columns: columns,
          updateRowsStatus: updateRowsStatus,
          originalData: data,
          predicate: predicate,
          dataConfig: dataConfig,
        }}
        initialState={{
          pagination: { paginationModel: { pageSize: 100 } },
        }}
        sx={{
          maxWidth: 'calc(100vw - 70px)',
          '& .MuiDataGrid-cell--editable': {
            backgroundColor: '#CBD5E1',
          },
          '& .MuiBox-root': {
            padding: '0px !important',
          },
        }}
      />
      <DownTimeWindowsDialog
        open={editingRow != null}
        resourceLabel={editingRow?.resource ?? ''}
        initialValue={editingRow?.downTime}
        comments={editingComments}
        currentUserEmail={currentUserEmail}
        commentContext={
          commentsEnabled && editingRow
            ? {
                schedulerId: schedulerId as string,
                iteration: iteration as string,
                weekNumber: selectedWeek ?? 1,
                resourceName: editingRow.resource,
                resourceType: editingRow.resourceType,
              }
            : undefined
        }
        onClose={() => setEditingRow(null)}
        onSave={(value) => {
          if (editingRow) {
            handleDataUpdate({
              [editingRow.id]: { ...editingRow, downTime: value },
            })
          }
          setEditingRow(null)
        }}
      />
    </Box>
  )
}
